import React, { useEffect, useState } from "react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { notify } from "../../../helpers";
import Card from "../../Card";
import {Hospital, LoaderCircle, MapPin, Search } from "lucide-react";
import Grid from "../../Grid";
import {
  fetchAllPracticeAndLocation,
  changePracticeStatus,
  changePracticeLocationStatus,
} from "../../../services/practice";

export default function PracticeManagement() {
  useDocumentTitle("Practice Management");
  const [practiceAndLocationData, setPracticeAndLocationData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all practices and locations
  const loadAllPracticeAndLocation = async () => {
    try {
      const res = await fetchAllPracticeAndLocation(true, true);
      if (res) {
        if (res?.detail) {
          setPracticeAndLocationData([]);
          console.log("🚀 ~ loadAllPracticeAndLocation ~ error:", res);
        } else {
          setPracticeAndLocationData(res.data);
        }
      }
    } catch (error) {
      setPracticeAndLocationData([]);
      // console.log("🚀 ~ loadAllPracticeAndLocation ~ error:", error);
    }finally {
      setIsLoading(false);
    }
  };

  // Handle practice status change
  const handlePracticeStatus = async ({ account_id, status }) => {
    const newChangePracticeData = {
      status: status || "active",
    };

    const response = await changePracticeStatus(account_id, newChangePracticeData);
    if (response.data) {
      notify("success", response.data.message);

      loadAllPracticeAndLocation();
    } else if (response?.error === true) {
      notify("error", response.message.data.detail || "Error Occurred!!");
    } else {
      notify("error", "Something Went Wrong!!");
    }
  };

  // Handle practice location status change
  const handlePracticeLocationStatus = async ({ location_id, status }) => {
    const newChangePracticeLocationData = {
      status: status || "active",
    };

    const response = await changePracticeLocationStatus(
      location_id,
      newChangePracticeLocationData
    );

    if (response.data) {
      notify("success", response.data.message);

      loadAllPracticeAndLocation();
    } else if (response?.error === true) {
      notify("error", response.message.data.detail || "Error Occurred!!");
    } else {
      notify("error", "Something Went Wrong!!");
    }
  };

  useEffect(() => {
    loadAllPracticeAndLocation(true, true);
  }, []);

  return (
    <>
      <div
        className="flex items-center justify-between"
        style={{ justifyContent: "space-between", gap: "15px 20px" }}
      >
        <h2 className="docpro_fs_24 docpro_mb_0">Practice Management</h2>
      </div>
      <Spacing lg="20" sm="20" />
      <Card>
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>
        </div>
        <Spacing lg="20" sm="20" />
        {isLoading ? (
          <div className="flex items-center loading" style={{ width: '100%', height: '100%', minHeight: '120px', justifyContent: 'center', background: 'var(--white)', borderRadius: '12px' }}>
            <span className="docpro_loading" style={{ position: 'inherit' }}>
              <LoaderCircle width={40} height={40} style={{color:"#369eff"}} />
            </span>
          </div>
        ) : (<>
<Grid variant="docpro_grid_gap_35_12 docpro_grid_col_4">
        {practiceAndLocationData?.map((account) => (
            <Card
              key={account.account_id}
              variant="docpro_card_md docpro_card_"
            >
              <div className="">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <h3 className="docpro_fs_16 docpro_mb_0 docpro_logo_color">
                    <Hospital width={20} height={20} />{" "}
                    <strong
                      className="docpro_fw_700 docpro_secondary_color"
                      style={{ fontFamily: "var(--primary-font)" }}
                    >
                      {account.account_name}
                    </strong>
                  </h3>
                  <label
                    className="switch"
                    onChange={(e) =>
                      handlePracticeStatus({
                        account_id: account.account_id,
                        status: e.target.checked ? "active" : "inactive",
                      })
                    }
                  >
                    <input
                      defaultChecked={account.status === "active"}
                      type="checkbox"
                      className="docpro_logo_color"
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div
                  style={{
                    width: "100%",
                    flexGrow: 1,
                    height: "2px",
                    backgroundColor: "var(--vivid)",
                    marginTop: "10px",
                  }}
                />
              </div>
              <Spacing lg="7" sm="7" />
              <div className="">
                {account.locations && (
                  <>
                    {Object.entries(account.locations).map(
                      ([locationId, location]) => (
                        <div
                          key={locationId}
                          style={{
                            marginTop: "5px",
                            marginBottom: "7px",
                            marginLeft: "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <h3 className="docpro_fs_14 docpro_mb_0 docpro_ternary_color">
                            <MapPin width={20} height={20} />{" "}
                            <strong className="docpro_fw_200 docpro_ternary_color">
                              {location.location_name}
                            </strong>
                          </h3>
                          <label
                            className="switch"
                            style={{
                              display: "inline-block",
                              width: "32px",
                              height: "14px",
                            }}
                            onChange={(e) =>
                              handlePracticeLocationStatus({
                                location_id: locationId,
                                status: e.target.checked
                                  ? "active"
                                  : "inactive",
                              })
                            }
                          >
                            <input
                              type="checkbox"
                              style={{
                                opacity: "0",
                                width: "0",
                                height: "0",
                              }}
                              defaultChecked={location.status === "active"}
                              className="docpro_logo_color"
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
              <Spacing lg="4" sm="4" />
            </Card>
          ))}
        </Grid>
        </>)}
        <Spacing lg="35" sm="35" />
      </Card>
    </>
  );
}
